// frontend/src/pages/PaymentSuccess.jsx

import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { verifyPayment } from "../services/stripeService";
import { Button } from "../components/button";
import { Spinner } from "../components/spinner";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/solid";
import stripeService from "../services/stripeService";

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("verifying"); // 'verifying', 'success', 'failed'
  const [paymentDetails, setPaymentDetails] = useState(null);

  useEffect(() => {
    const verifyPayment = async (retryCount = 0) => {
      try {
        const sessionId = searchParams.get("session_id");
        if (!sessionId) {
          setStatus("failed");
          return;
        }

        const result = await stripeService.verifyPayment(sessionId);
        console.log("Verification result:", result); // Debug log

        if (result && result.success) {
          setPaymentDetails({
            email: result.customer_email,
            amount: (result.amount_total / 100).toFixed(2), // Convert cents to dollars
            status: result.payment_status,
          });
          setStatus("success");
        } else if (retryCount < 3) {
          console.log(`Retry attempt ${retryCount + 1}`); // Debug log
          setTimeout(() => verifyPayment(retryCount + 1), 2000);
        } else {
          setStatus("failed");
        }
      } catch (error) {
        console.error("Payment verification failed:", error);
        if (retryCount < 3) {
          setTimeout(() => verifyPayment(retryCount + 1), 2000);
        } else {
          setStatus("failed");
        }
      }
    };

    verifyPayment();
  }, [searchParams]);

  const renderContent = () => {
    switch (status) {
      case "verifying":
        return (
          <div className="text-center">
            <Spinner className="mx-auto h-12 w-12 text-blue-500" />
            <p className="text-lg text-gray-600">Verifying your payment...</p>
          </div>
        );
      case "success":
        return (
          <div className="text-center">
            <CheckCircleIcon className="mx-auto h-12 w-12 text-green-500" />
            <h1 className="mt-4 text-2xl font-semibold text-gray-900">
              Payment Successful!
            </h1>
            {paymentDetails && (
              <div className="mt-4 text-left bg-gray-50 p-4 rounded-md">
                <p className="text-sm text-gray-600">
                  Amount paid: ${paymentDetails.amount}
                </p>
                <p className="text-sm text-gray-600">
                  Confirmation sent to: {paymentDetails.email}
                </p>
              </div>
            )}
            <p className="mt-4 text-gray-600">
              Thank you for your purchase. You now have access to all premium
              features!
            </p>
            <div className="mt-6 flex justify-center space-x-4">
              <Button
                variant="primary"
                onClick={() => navigate("/resumes")}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Start Creating Resumes
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/profile")}
                className="border-gray-300 hover:bg-gray-50"
              >
                View Profile
              </Button>
            </div>
          </div>
        );
      case "failed":
        return (
          <div className="text-center">
            <XCircleIcon className="mx-auto h-12 w-12 text-red-500" />
            <h1 className="mt-4 text-2xl font-semibold text-gray-900">
              Payment Verification Failed
            </h1>
            <p className="mt-2 text-gray-600">
              We couldn't verify your payment. If you believe this is an error,
              please contact our support team.
            </p>
            <div className="mt-6 flex justify-center space-x-4">
              <Button
                variant="primary"
                onClick={() => window.location.reload()}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Try Again
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/profile")}
                className="border-gray-300 hover:bg-gray-50"
              >
                Back to Profile
              </Button>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="max-w-md w-full bg-white shadow-md rounded-lg p-8">
        {renderContent()}
      </div>
    </div>
  );
}
